<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between">
          <span><i class="fa fa-history"></i> My Activity Log</span>
          <button
            v-if="moderationModeEnabled"
            type="button"
            class="btn btn-primary btn-sm"
            @click="openManualEntryModal">
            <i class="fa fa-plus"></i>
            Add Entry
          </button>
        </div>
        <div class="card-body">
          <div class="activity-summary" :class="missingReasons > 0 ? 'activity-summary-warning' : 'activity-summary-complete'">
            <i :class="missingReasons > 0 ? 'fa fa-exclamation-triangle' : 'fa fa-check-circle'"></i>
            <span v-if="missingReasons > 0">
              {{ missingReasons }} important {{ missingReasons === 1 ? 'reason is' : 'reasons are' }} missing.
            </span>
            <span v-else>All important entries have a reason.</span>
          </div>

          <div class="audit-filter-bar">
            <div class="audit-filter-toggles">
              <label class="audit-filter-toggle">
                <input v-model="displayImportant" type="checkbox">
                <span>Important entries</span>
              </label>
              <label class="audit-filter-toggle">
                <input v-model="displayReason" type="checkbox">
                <span>Missing reasons</span>
              </label>
              <label class="audit-filter-toggle">
                <input v-model="includeTestOnly" type="checkbox">
                <span>Include Test-only</span>
              </label>
              <label v-if="canUseModerationMode" class="audit-filter-toggle">
                <input v-model="groupsEnabled" type="checkbox">
                <span>Moderation Mode</span>
              </label>
            </div>

            <div class="audit-filter-field">
              <label>Search</label>
              <input
                v-model="filterQuery"
                type="text"
                class="form-control"
                placeholder="Reason, entry, group or field...">
            </div>

            <div class="audit-filter-field">
              <label>Filter by Action</label>
              <select v-model="filterbyAction" class="form-control">
                <option value="">All</option>
                <template v-for="group in actionGroups">
                  <option :key="group.label" disabled class="audit-action-category">{{ group.label }}</option>
                  <option
                    v-for="action in group.options"
                    :key="group.label + '-' + action.value"
                    :value="action.value">
                    {{ action.label }}
                  </option>
                </template>
              </select>
            </div>
          </div>

          <b-overlay
            :show="logLoading"
            variant="dark"
            opacity="0.55"
            blur="1px"
            rounded="sm">
            <template #overlay>
              <div class="text-center text-light">
                <b-spinner variant="info"></b-spinner>
                <div class="mt-2">Loading activity log...</div>
              </div>
            </template>
            <div :class="{ 'audit-log-loading-surface': logLoading && entries.length === 0 }">
              <AuditLogList
                :entries="entries"
                :groups="groups"
                :default-fields="defaultFields"
                :groups-enabled="moderationModeEnabled"
                :search-query="filterQuery"
                :editable-groups="moderationModeEnabled"
                :editable-reasons="true"
                :editable-evidence="true"
                :allow-delete-manual-entries="true"
                :delete-entries-from-user-log="true"
                :pinned-group-ids="pinnedGroupIDs"
                @evidence-changed="fetchLog(false)"
                @pinned-group-ids-changed="updatePinnedGroupIDs">
              </AuditLogList>
            </div>
          </b-overlay>

          <b-modal
            v-model="manualEntryModal.visible"
            title="Add Activity Log Entry"
            centered
            header-bg-variant="dark"
            header-text-variant="light"
            body-bg-variant="dark"
            body-text-variant="light"
            footer-bg-variant="dark"
            footer-text-variant="light"
            :ok-title="manualEntryModal.submitting ? 'Adding...' : 'Add Entry'"
            :ok-disabled="!manualEntryCanSubmit || manualEntryModal.submitting"
            :cancel-disabled="manualEntryModal.submitting"
            :no-close-on-backdrop="manualEntryModal.submitting"
            :no-close-on-esc="manualEntryModal.submitting"
            :hide-header-close="manualEntryModal.submitting"
            @ok.prevent="createManualEntry">
            <div v-if="manualEntryModal.error" class="alert alert-danger">
              {{ manualEntryModal.error }}
            </div>
            <label for="manual-entry-username">Username</label>
            <input
              id="manual-entry-username"
              v-model="manualEntryModal.username"
              type="text"
              class="form-control"
              maxlength="64"
              autocomplete="off"
              :disabled="manualEntryModal.submitting"
              @input="manualEntryModal.error = ''">

            <label class="mt-3" for="manual-entry-type">Entry Type</label>
            <select
              id="manual-entry-type"
              v-model="manualEntryModal.banType"
              class="form-control"
              :disabled="manualEntryModal.submitting">
              <option value="W">Warning</option>
              <template v-if="canCreateBanEntries">
                <option value="P">Permanent Ban</option>
                <option value="T">Temporary Ban</option>
                <option value="E">Trade Ban</option>
                <option value="M">Quiet Ban</option>
                <option value="X">PVP Ban</option>
              </template>
            </select>

            <label class="mt-3" for="manual-entry-reason">Reason</label>
            <textarea
              v-audit-resize="'reason'"
              id="manual-entry-reason"
              v-model="manualEntryModal.reason"
              class="form-control"
              rows="3"
              maxlength="2000"
              :disabled="manualEntryModal.submitting"
              @input="manualEntryModal.error = ''">
            </textarea>
          </b-modal>

          <b-pagination
            v-if="count > 100"
            v-model="page"
            class="mt-3"
            size="md"
            :total-rows="count"
            :per-page="100"
            @change="fetchPage">
          </b-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import AuditLogList from '../../components/AuditLog/AuditLogList.vue'
import { buildAuditLogActionGroups } from '../../auditlog_permissions'

const groupsStorageKey = 'modtools_userlog_groups_enabled'
const pinnedGroupsStorageKey = 'modtools_userlog_pinned_group_ids'

function storedBoolean (key, fallback) {
  const value = localStorage.getItem(key)
  if (value === null) {
    return fallback
  }
  return value === 'true'
}

function storedPinnedGroupIDs () {
  try {
    const value = JSON.parse(localStorage.getItem(pinnedGroupsStorageKey) || '[]')
    return Array.isArray(value)
      ? value.map(Number).filter(groupID => Number.isInteger(groupID) && groupID > 0).slice(0, 25)
      : []
  } catch (error) {
    return []
  }
}

export default {
  name: 'Userlog',
  components: {
    AuditLogList
  },
  data () {
    return {
      displayImportant: false,
      displayReason: false,
      includeTestOnly: false,
      groupsEnabled: storedBoolean(groupsStorageKey, false),
      pinnedGroupIDs: storedPinnedGroupIDs(),
      filterQuery: '',
      filterbyAction: '',
      page: 1,
      filterTimer: null,
      manualEntryModal: {
        visible: false,
        username: '',
        reason: '',
        banType: 'W',
        submitting: false,
        error: ''
      }
    }
  },
  computed: {
    ...mapGetters({
      entries: 'Auditlog',
      groups: 'AuditlogGroups',
      defaultFields: 'AuditlogDefaultFields',
      groupRevision: 'AuditlogGroupRevision',
      logLoading: 'AuditlogLoading',
      manualEntryResult: 'AuditlogManualEntryResult',
      count: 'LogCount',
      missingReasons: 'AuditlogMissingReasons'
    }),
    actionGroups () {
      return buildAuditLogActionGroups(permission => this.hasPermission(permission))
    },
    canUseModerationMode () {
      return this.hasPermission('ModerationMode')
    },
    moderationModeEnabled () {
      return this.canUseModerationMode && this.groupsEnabled
    },
    canCreateBanEntries () {
      return this.hasPermission('UserBan')
    },
    manualEntryCanSubmit () {
      return Boolean(
        this.manualEntryModal.username.trim() &&
        this.manualEntryModal.reason.trim()
      )
    }
  },
  watch: {
    displayImportant () {
      this.updateLog()
    },
    displayReason () {
      this.updateLog()
    },
    includeTestOnly () {
      this.updateLog()
    },
    filterbyAction () {
      this.updateLog()
    },
    filterQuery () {
      this.scheduleUpdate()
    },
    groupsEnabled (enabled) {
      localStorage.setItem(groupsStorageKey, String(this.canUseModerationMode && enabled))
      this.updateLog()
    },
    groupRevision () {
      this.fetchLog(false)
    },
    manualEntryResult (result) {
      if (!this.manualEntryModal.submitting || !result || !result.revision) {
        return
      }
      this.manualEntryModal.submitting = false
      if (result.ok) {
        this.manualEntryModal.visible = false
        return
      }
      this.manualEntryModal.error = result.error || 'Could not create the entry.'
    }
  },
  created () {
    this.clearAuditlog()
  },
  mounted () {
    this.fetchLog(true)
  },
  beforeDestroy () {
    window.clearTimeout(this.filterTimer)
  },
  methods: {
    ...mapActions(['clearAuditlog', 'startAuditlogLoading']),
    updateLog () {
      this.page = 1
      this.fetchLog(true)
    },
    scheduleUpdate () {
      window.clearTimeout(this.filterTimer)
      this.filterTimer = window.setTimeout(() => this.updateLog(), 250)
    },
    fetchPage (page) {
      this.page = page
      this.scrollToPageTop()
      this.fetchLog(false)
    },
    scrollToPageTop () {
      this.$nextTick(() => {
        let element = this.$el
        while (element) {
          if (element.scrollHeight > element.clientHeight) {
            element.scrollTop = 0
          }
          element = element.parentElement
        }
        if (window.scrollTo) {
          window.scrollTo(0, 0)
        }
      })
    },
    fetchLog (count) {
      this.startAuditlogLoading()
      this.emit('getAuditlog', {
        page: this.page,
        count,
        display_only_important: this.displayImportant,
        display_only_reasons: this.displayReason,
        include_test_only: this.includeTestOnly,
        filterbyAction: this.filterbyAction,
        user: true,
        filter_by_user: '',
        filter_by_search: this.filterQuery.trim(),
        pinned_group_ids: this.pinnedGroupIDs,
        moderation_mode: this.moderationModeEnabled
      })
    },
    updatePinnedGroupIDs (groupIDs) {
      this.pinnedGroupIDs = groupIDs
      localStorage.setItem(pinnedGroupsStorageKey, JSON.stringify(groupIDs))
      this.fetchLog(false)
    },
    openManualEntryModal () {
      this.manualEntryModal = {
        visible: true,
        username: '',
        reason: '',
        banType: 'W',
        submitting: false,
        error: ''
      }
    },
    createManualEntry () {
      const username = this.manualEntryModal.username.trim()
      const reason = this.manualEntryModal.reason.trim()
      if (!username || !reason) {
        return
      }
      this.manualEntryModal.error = ''
      this.manualEntryModal.submitting = true
      this.emit('createManualAuditLogEntry', {
        username,
        reason,
        ban_type: this.manualEntryModal.banType
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.audit-log-loading-surface {
  min-height: 180px;
}

.activity-summary {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 10px 12px;
  margin-bottom: 14px;
  border-left: 3px solid;
  background: rgba(255, 255, 255, 0.035);
}

.activity-summary-warning {
  border-color: #f86c6b;
  color: #ff9a99;
}

.activity-summary-complete {
  border-color: #4dbd74;
  color: #79d69a;
}

.audit-filter-bar {
  display: grid;
  grid-template-columns: minmax(300px, 1.4fr) repeat(2, minmax(220px, 1fr));
  gap: 14px;
  align-items: end;
  padding: 14px;
  margin-bottom: 18px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.audit-filter-toggles {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.audit-filter-toggle {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 34px;
  margin: 0;
  padding: 7px 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.16);
  font-size: 0.82rem;
}

.audit-filter-toggle input {
  margin: 0;
}

.audit-filter-field label {
  display: block;
  margin-bottom: 5px;
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
}

.audit-action-category {
  color: #8f9ba6;
  background: #1b2229;
}

@media (max-width: 1199px) {
  .audit-filter-bar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .audit-filter-bar {
    grid-template-columns: 1fr;
  }
}
</style>
